import { useState, useMemo, useRef, useCallback, useEffect, memo } from 'react';
import { InputNumber, Input, Select, Empty, Pagination } from 'antd';

// ════════════════════════════════════════════════════════════════════
// Stage 6PL-1 — the rate tabs, rebuilt in the PROFORMA GRID's language.
//
// WHY NOT AN AntD TABLE (which is what this replaced)
//   A price list is entered the same way a proforma is: one row per master
//   row, the same handful of columns, typed straight down the page — and
//   often a hundred rows at a sitting after a negotiation. An AntD Table
//   gives a form control per cell with its own border and padding, so the
//   eye has to re-find the column on every row, and the keyboard cannot
//   cross a cell boundary. The proforma grid solved that already; a second
//   grammar for the same job on the master screen is just a second thing to
//   learn.
//
//   So this deliberately copies pfg-*: 28px rows, the two-row grouped
//   header, borderless cells that take a blue inset ring on focus, a sticky
//   serial column, a sticky delete rail, arrow-key navigation and Ctrl+'
//   fill-down. Same muscle memory, same pixels.
//
// WHAT IT DOES **NOT** COPY, on purpose
//   The tree. Proforma rows nest (SHEET masters, LAMI/DGU plies) and most of
//   that file's 2,400 lines exist to serve it. A rate row is flat: one master
//   row, one rate. Copying the nesting machinery here would be ceremony
//   around a list.
//
// THE GROUPED HEADER EARNS ITS KEEP
//   It separates what the MASTER says from what THIS CARD says — which is
//   the single question being answered on this screen. The master columns are
//   read-only and untinted; the card's own columns are tinted and editable.
//   You can see at a glance which numbers you are responsible for.
// ════════════════════════════════════════════════════════════════════

const ROW_H = 28;
const GROUP_H = 22;
const LEAF_H = 24;
const HEADER_H = GROUP_H + LEAF_H;
const PAGE_SIZES = [25, 50, 100, 200];   // no "all" — the page size IS the render cap

const GRID_FONT = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',Arial,'Noto Sans',sans-serif";

const GRID_CSS = `
.plg-wrap{font-family:${GRID_FONT};color:#0f172a;}
.plg-scroll{position:relative;overflow:auto;max-height:max(420px, calc(100vh - 460px));border:1px solid #cbd5e1;background:#fff;border-radius:6px;box-shadow:0 1px 2px rgba(15,23,42,.04);}
.plg-scroll::-webkit-scrollbar{width:11px;height:11px;}
.plg-scroll::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:6px;border:3px solid #fff;}
.plg-scroll::-webkit-scrollbar-thumb:hover{background:#94a3b8;}
.plg-head{position:sticky;top:0;z-index:8;}
.plg-hrow{display:flex;}
.plg-ghdr,.plg-lhdr{display:flex;flex:0 0 auto;align-items:center;justify-content:center;box-sizing:border-box;white-space:nowrap;overflow:hidden;text-transform:uppercase;}
.plg-ghdr{background:#eef2f7;color:#475569;font-size:10px;font-weight:700;letter-spacing:.05em;border-right:1px solid #dbe2ea;border-bottom:1px solid #cbd5e1;}
.plg-lhdr{background:#f6f8fb;color:#475569;font-size:10px;font-weight:600;letter-spacing:.03em;border-right:1px solid #e6ebf1;border-bottom:1px solid #cbd5e1;}
.plg-lhdr.ch,.plg-ghdr.ch{background:#e9f1ff;}
.plg-lhdr.bd,.plg-ghdr.bd{background:#fff5e9;}
.plg-row{display:flex;position:relative;box-sizing:border-box;background:#fff;}
.plg-row:hover{background:#f5f8fc;}
.plg-row:focus-within .plg-serial{background:#dbeafe;color:#1d4ed8;font-weight:700;}
.plg-cell{position:relative;flex:0 0 auto;box-sizing:border-box;height:100%;border-right:1px solid #eef1f5;border-bottom:1px solid #eef1f5;overflow:hidden;display:flex;align-items:center;background:inherit;}
.plg-cell.ch{background:#f6faff;}
.plg-cell.bd{background:#fffbf4;}
.plg-serial{position:sticky;left:0;z-index:6;background:#f8fafc;color:#94a3b8;font-size:10.5px;font-variant-numeric:tabular-nums;justify-content:center;border-right:1px solid #e2e8f0;}
.plg-del{position:sticky;right:0;z-index:6;background:inherit;border-left:1px solid #cbd5e1;justify-content:center;}
.plg-delbtn{width:100%;height:100%;border:none;background:transparent;cursor:pointer;color:#cbd5e1;font-size:13px;line-height:1;display:flex;align-items:center;justify-content:center;padding:0;}
.plg-row:hover .plg-delbtn{color:#94a3b8;}
.plg-delbtn:hover{color:#dc2626;background:#fef2f2;}
.plg-ro{width:100%;padding:0 8px;font-size:11.5px;font-variant-numeric:tabular-nums;color:#64748b;}
.plg-ro.r{text-align:right;}
.plg-ro.strong{color:#0f172a;font-weight:600;}
.plg-ro.warn{color:#b45309;font-weight:600;}
.plg-ro.good{color:#15803d;font-weight:600;}
.plg-ro.mute{color:#cbd5e1;}
.plg-cell .ant-input,.plg-cell .ant-input-number,.plg-cell .ant-select-selector{height:26px!important;border:none!important;border-radius:0!important;box-shadow:none!important;background:transparent!important;}
.plg-cell .ant-input-number-input,.plg-cell .ant-input,.plg-cell .ant-select-selection-item,.plg-cell .ant-select-selection-search-input,.plg-cell .ant-select-selection-placeholder{height:26px!important;line-height:26px!important;font-size:11.5px!important;padding:0 8px!important;}
.plg-cell .ant-input-number-input{font-variant-numeric:tabular-nums;text-align:right;}
.plg-cell .ant-input-number{width:100%;}
.plg-cell .ant-input-number-handler-wrap{display:none;}
.plg-cell .ant-select{width:100%;}
.plg-cell .ant-select-selector{padding:0!important;}
.plg-cell .ant-input:focus,.plg-cell .ant-input-number-focused,.plg-cell .ant-input-number:focus-within,.plg-cell .ant-select-focused .ant-select-selector{background:#fff!important;box-shadow:inset 0 0 0 2px #2563eb!important;border-radius:2px!important;z-index:4;}
.plg-cell .ant-select-arrow{right:4px;font-size:9px;color:#cbd5e1;}
.plg-row:hover .plg-cell .ant-select-arrow{color:#94a3b8;}
.plg-empty{padding:28px 0;}
.plg-ctx{position:fixed;z-index:200;background:#fff;border:1px solid #e2e8f0;border-radius:9px;box-shadow:0 12px 34px rgba(15,23,42,.16),0 2px 8px rgba(15,23,42,.06);min-width:238px;overflow:hidden;padding:3px 4px;font-family:${GRID_FONT};}
.plg-ctx button{display:flex;align-items:center;justify-content:space-between;gap:16px;width:100%;border:none;background:transparent;padding:4px 10px;font-size:12px;line-height:1.2;cursor:pointer;color:#1e293b;border-radius:5px;transition:background .08s;}
.plg-ctx button:hover{background:#f1f5f9;}
.plg-ctx button.del{color:#dc2626;}
.plg-ctx button.del:hover{background:#fef2f2;}
.plg-ctx button:disabled{color:#cbd5e1;cursor:default;}
.plg-ctx button:disabled:hover{background:transparent;}
.plg-ctx .sep{height:1px;background:#eef2f7;margin:3px 6px;}
.plg-ctx kbd{flex:0 0 auto;font-size:9px;color:#94a3b8;font-family:${GRID_FONT};background:#f8fafc;border:1px solid #e6ebf1;border-radius:4px;padding:1px 6px;letter-spacing:.02em;}
.plg-foot{display:flex;align-items:center;gap:14px;padding:6px 2px 0;font-size:10.5px;color:#94a3b8;flex-wrap:wrap;}
.plg-foot kbd{font-size:9px;color:#64748b;background:#f8fafc;border:1px solid #e6ebf1;border-radius:4px;padding:1px 5px;font-family:${GRID_FONT};}
.plg-addbtn{border:1px dashed #cbd5e1;background:#fff;color:#475569;border-radius:6px;height:28px;padding:0 14px;font-size:11.5px;cursor:pointer;font-family:${GRID_FONT};}
.plg-addbtn:hover{border-color:#2563eb;color:#1d4ed8;background:#f8fbff;}
`;

// Cell ids are namespaced PER GRID INSTANCE, not just by row/column. AntD Tabs
// keep a pane mounted once it has been visited, so after clicking through the
// five rate tabs there are five grids in the DOM at once — bare `plc-0-1` ids
// would collide and getElementById would hand arrow-key focus to whichever tab
// rendered first. (Found by the UI probe; a duplicate id is invisible until
// something navigates by it.)
let GRID_SEQ = 0;
const cellId = (gid, r, c) => `plc-${gid}-${r}-${c}`;
function parseCellId(id) {
  const m = /^plc-(\d+)-(\d+)-(\d+)$/.exec(id || '');
  return m ? { gid: parseInt(m[1], 10), r: parseInt(m[2], 10), c: parseInt(m[3], 10) } : null;
}

const money = (v) => (v == null || v === '' || Number.isNaN(Number(v))
  ? '—'
  : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/**
 * The column set for one rate kind. PROCESS gains a thickness, ITEM gains the
 * min/max band — both are kind-scoped in the validator and in migration 0181,
 * so showing them everywhere would offer a cell that can only be rejected.
 */
function buildColumns(kind) {
  const cols = [
    { key: 'ref', group: 'master', title: kind === 'ITEM' ? 'Item' : 'Master row', width: 280, kind: 'ref', align: 'left' }
  ];
  if (kind === 'PROCESS') {
    cols.push({ key: 'thicknessMm', group: 'master', title: 'Thk', width: 74, kind: 'num', align: 'right' });
  }
  cols.push({ key: 'masterRate', group: 'master', title: 'Master rate', width: 104, kind: 'ro', align: 'right' });
  cols.push({ key: 'rate', group: 'list', title: 'This list', width: 104, kind: 'num', align: 'right', tint: 'ch' });
  cols.push({ key: 'priceUomCode', group: 'list', title: 'Unit', width: 76, kind: 'text', align: 'left', tint: 'ch' });
  cols.push({ key: 'delta', group: 'list', title: 'Δ', width: 78, kind: 'ro', align: 'right', tint: 'ch' });
  if (kind === 'ITEM') {
    cols.push({ key: 'minRate', group: 'band', title: 'Min', width: 82, kind: 'num', align: 'right', tint: 'bd' });
    cols.push({ key: 'maxRate', group: 'band', title: 'Max', width: 82, kind: 'num', align: 'right', tint: 'bd' });
  }
  cols.push({ key: 'note', group: 'note', title: 'Note', width: 230, kind: 'text', align: 'left' });
  return cols;
}

const GROUP_TITLE = {
  master: 'From the master',
  list: 'This price list',
  band: 'Band',
  note: ''
};
const GROUP_TINT = { list: 'ch', band: 'bd' };

const NAV_KINDS = ['ref', 'num', 'text'];

/**
 * Colour for the Δ column. A rate EQUAL to the master is neither a discount nor
 * a premium, so it stays neutral — colouring it amber would flag every
 * unchanged row as though something needed looking at, and the column's whole
 * job is to make the rows that DID move findable.
 */
function deltaClass(delta) {
  if (delta == null) return ' mute';
  if (Math.abs(delta) < 0.05) return '';
  return delta < 0 ? ' good' : ' warn';
}

// ── one row ─────────────────────────────────────────────────────────
const GridRow = memo(function GridRow({
  row, rowIdx, gid, columns, navIdxOf, opt, readOnly, totalWidth,
  onPatch, onRemove, onContext, options, onSearchItems, kindKey
}) {
  const delta = (() => {
    if (!opt || opt.masterRate == null || !(Number(opt.masterRate) > 0) || row.rate == null) return null;
    return ((Number(row.rate) - Number(opt.masterRate)) / Number(opt.masterRate)) * 100;
  })();
  const belowBand = opt && opt.minRate != null && row.rate != null
    && Number(row.rate) < Number(opt.minRate);

  return (
    <div
      className="plg-row"
      style={{ width: totalWidth, height: ROW_H }}
      onContextMenu={(e) => onContext(e, rowIdx)}
    >
      <div className="plg-cell plg-serial" style={{ width: 40 }}>{rowIdx + 1}</div>

      {columns.map((c) => {
        const navIdx = navIdxOf[c.key];
        const id = navIdx >= 0 ? cellId(gid, rowIdx, navIdx) : undefined;
        const cls = `plg-cell${c.tint ? ` ${c.tint}` : ''}`;

        if (c.key === 'masterRate') {
          return (
            <div key={c.key} className={cls} style={{ width: c.width }}>
              <span className="plg-ro r">{opt ? money(opt.masterRate) : '—'}</span>
            </div>
          );
        }
        if (c.key === 'delta') {
          return (
            <div key={c.key} className={cls} style={{ width: c.width }}>
              <span className={`plg-ro r${deltaClass(delta)}`}>
                {delta == null ? '—' : `${delta >= 0 ? '+' : ''}${delta.toFixed(1)}%`}
              </span>
            </div>
          );
        }

        if (readOnly) {
          let txt = row[c.key];
          if (c.key === 'ref') txt = opt ? opt.label : '(removed from master)';
          else if (c.kind === 'num') txt = c.key === 'thicknessMm' ? (txt == null ? 'all' : `${txt}`) : money(txt);
          return (
            <div key={c.key} className={cls} style={{ width: c.width }}>
              <span className={`plg-ro${c.align === 'right' ? ' r' : ''}${c.key === 'rate' ? ' strong' : ''}`}>
                {txt == null || txt === '' ? '—' : txt}
              </span>
            </div>
          );
        }

        if (c.key === 'ref') {
          return (
            <div key={c.key} className={cls} style={{ width: c.width }}>
              <Select
                id={id}
                showSearch
                allowClear
                size="small"
                variant="borderless"
                value={row.refId || undefined}
                placeholder="Select…"
                popupMatchSelectWidth={380}
                filterOption={kindKey === 'ITEM' ? false : undefined}
                optionFilterProp={kindKey === 'ITEM' ? undefined : 'label'}
                onSearch={kindKey === 'ITEM' ? onSearchItems : undefined}
                options={(options || []).map((o) => ({
                  value: o.id,
                  label: o.code ? `${o.label}  ·  ${o.code}` : o.label,
                  disabled: o.locked === true
                }))}
                onChange={(val) => {
                  const picked = (options || []).find((o) => o.id === val);
                  // Pre-fill the unit from the master: a rate saved without one
                  // is wrong by 10.764× the moment the document flips SQM/SQF.
                  onPatch(rowIdx, {
                    refId: val || null,
                    priceUomCode: picked ? picked.uom : null,
                    minRate: null, maxRate: null
                  });
                }}
              />
            </div>
          );
        }

        if (c.kind === 'num') {
          const isThk = c.key === 'thicknessMm';
          return (
            <div key={c.key} className={cls} style={{ width: c.width }}>
              <InputNumber
                id={id}
                size="small"
                variant="borderless"
                controls={false}
                value={row[c.key]}
                min={isThk ? 0.01 : 0}
                precision={isThk ? undefined : 2}
                placeholder={isThk ? 'all' : ''}
                status={c.key === 'rate' && belowBand ? 'warning' : undefined}
                onChange={(val) => onPatch(rowIdx, { [c.key]: val })}
              />
            </div>
          );
        }

        return (
          <div key={c.key} className={cls} style={{ width: c.width }}>
            <Input
              id={id}
              size="small"
              variant="borderless"
              value={row[c.key] || ''}
              placeholder={c.key === 'note' ? 'Why this rate was agreed' : ''}
              onChange={(e) => {
                const v = e.target.value;
                onPatch(rowIdx, { [c.key]: c.key === 'priceUomCode' ? v.toUpperCase() : v });
              }}
            />
          </div>
        );
      })}

      {!readOnly && (
        <div className="plg-cell plg-del" style={{ width: 32 }}>
          <button
            type="button"
            className="plg-delbtn"
            title="Remove this rate"
            onClick={() => onRemove(rowIdx)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
});

// ── the grid ────────────────────────────────────────────────────────
export default function PriceListRateGrid({
  kindKey, options, rows, readOnly, onSearchItems,
  onPatchRow, onRemoveRow, onInsertRow, onDuplicateRow
}) {
  // A stable per-instance id, assigned once on first render.
  const gidRef = useRef(null);
  if (gidRef.current === null) { GRID_SEQ += 1; gidRef.current = GRID_SEQ; }
  const gid = gidRef.current;

  const columns = useMemo(() => buildColumns(kindKey), [kindKey]);
  const navKeys = useMemo(() => columns.filter((c) => NAV_KINDS.includes(c.kind)).map((c) => c.key), [columns]);
  const navIdxOf = useMemo(() => {
    const m = {};
    for (const c of columns) m[c.key] = navKeys.indexOf(c.key);
    return m;
  }, [columns, navKeys]);

  const byRef = useMemo(() => {
    const m = new Map();
    for (const o of options || []) m.set(o.id, o);
    return m;
  }, [options]);

  const bodyWidth = useMemo(
    () => 40 + columns.reduce((s, c) => s + c.width, 0) + (readOnly ? 0 : 32),
    [columns, readOnly]
  );

  // ── grouped header ────────────────────────────────────────────────
  const groups = useMemo(() => {
    const out = [];
    for (const c of columns) {
      const last = out[out.length - 1];
      if (last && last.group === c.group) last.width += c.width;
      else out.push({ group: c.group, width: c.width });
    }
    return out;
  }, [columns]);

  // ── pagination ────────────────────────────────────────────────────
  // A card can legitimately hold hundreds of rates (that is what CSV import is
  // for), and 96 rows of inputs in one scroller is both slow and impossible to
  // hold a place in. Paging bounds what is mounted, so the row-windowing this
  // grid used to do is redundant — page size is the cap, and it tops out at 200.
  //
  // ⚠️ THE INDEX MUST STAY ABSOLUTE. Every handler here (patch / remove /
  // insert / duplicate) is given the row's index within this KIND's full list,
  // and the parent maps that back to its slot in the mixed `rates` array. Hand
  // it a page-relative index and editing the 1st row of page 2 would silently
  // rewrite the 1st row of page 1 — the wrong customer's rate, with nothing on
  // screen to show it. So `rowIdx = first + i` everywhere, never `i`.
  const scrollRef = useRef(null);
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  // Deleting the last row of the last page must not strand the view on a page
  // that no longer exists.
  useEffect(() => { if (page > pageCount) setPage(pageCount); }, [page, pageCount]);
  const first = (page - 1) * pageSize;
  const last = Math.min(rows.length, first + pageSize);

  // Focus that has to wait for a page change to render.
  const pendingFocus = useRef(null);
  useEffect(() => {
    if (!pendingFocus.current) return;
    const [r, c] = pendingFocus.current;
    pendingFocus.current = null;
    const el = document.getElementById(cellId(gid, r, c));
    if (el) { el.focus(); if (el.select) try { el.select(); } catch (_e) { /* not an input */ } }
  }, [page, pageSize, gid, rows.length]);

  /** Move focus to an absolute row, crossing a page boundary if needed. */
  const goToRow = useCallback((r, c) => {
    const target = Math.floor(r / pageSize) + 1;
    if (target !== page) { pendingFocus.current = [r, c]; setPage(target); return; }
    const el = document.getElementById(cellId(gid, r, c));
    if (el) { el.focus(); if (el.select) try { el.select(); } catch (_e) { /* not an input */ } }
  }, [gid, page, pageSize]);

  // ── keyboard: arrows / Enter / Ctrl+' fill-down / Ctrl+↕ insert ────
  const focusCell = useCallback((r, c) => {
    const el = document.getElementById(cellId(gid, r, c));
    if (el) { el.focus(); if (el.select) try { el.select(); } catch (_e) { /* Select has no select() */ } }
  }, [gid]);

  const onKeyDown = useCallback((e) => {
    const parsed = e.target && parseCellId(e.target.id);
    if (!parsed) return;
    // An open dropdown owns the arrow keys — stealing them would make the
    // picker unusable, which is the one cell where a list must be walkable.
    if (e.target.getAttribute && e.target.getAttribute('aria-expanded') === 'true'
        && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter')) return;
    if (parsed.gid !== gid) return;          // a keydown that belongs to another grid
    const { r, c } = parsed;

    if (e.ctrlKey || e.metaKey) {
      if (e.repeat) { e.preventDefault(); return; }   // a held key must not machine-gun row inserts
      // Ctrl+'  — fill down from the row above, same shortcut as the proforma
      // grid (Ctrl+D is the browser's bookmark).
      if (e.key === "'" && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        if (r > 0) {
          const key = navKeys[c];
          onPatchRow(r, { [key]: rows[r - 1][key] });
        }
        return;
      }
      if (e.key === 'ArrowDown' && !e.shiftKey) { e.preventDefault(); e.stopPropagation(); onInsertRow(r + 1); return; }
      if (e.key === 'ArrowUp' && !e.shiftKey) { e.preventDefault(); e.stopPropagation(); onInsertRow(r); return; }
      if (e.key === 'Delete' && !e.shiftKey) { e.preventDefault(); e.stopPropagation(); onRemoveRow(r); focusCell(r, c); return; }
      return;
    }

    // stopPropagation, not just preventDefault. preventDefault only cancels the
    // BROWSER's default action; the event still reaches the cell's own React
    // handler, and AntD's InputNumber steps its value on ArrowUp/ArrowDown. So
    // arrowing down a rate column silently decremented every rate it passed
    // through. Stopping in the CAPTURE phase keeps the event from reaching the
    // input at all. (The proforma grid does the same — found here by probe.)
    // Swallowed whether or not focus actually moves. In a grid the vertical
    // arrows are NAVIGATION, never a value stepper — so at the first and last
    // row they must do nothing at all. Letting them fall through handed the
    // event to AntD's InputNumber, so arrowing UP from the top row of a rate
    // column bumped that rate 452 -> 453, and arrowing DOWN past the bottom
    // turned an empty rate into 0.00. Both are silent edits to a price.
    if (e.key === 'ArrowDown' || e.key === 'Enter') {
      e.preventDefault(); e.stopPropagation();
      if (r + 1 < rows.length) goToRow(r + 1, c);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault(); e.stopPropagation();
      if (r > 0) goToRow(r - 1, c);
      return;
    }
    // Left/right only when the caret is already at the edge, or typing in a
    // text cell could never move within the word.
    const el = e.target;
    const atStart = el.selectionStart === 0 && el.selectionEnd === 0;
    const atEnd = el.value != null && el.selectionStart === String(el.value).length
      && el.selectionEnd === String(el.value).length;
    if (e.key === 'ArrowLeft' && atStart && c > 0) {
      e.preventDefault(); e.stopPropagation(); focusCell(r, c - 1); return;
    }
    if (e.key === 'ArrowRight' && atEnd && c + 1 < navKeys.length) {
      e.preventDefault(); e.stopPropagation(); focusCell(r, c + 1);
    }
  }, [gid, navKeys, rows, onPatchRow, onInsertRow, onRemoveRow, focusCell, goToRow]);

  /** Append a rate and land the cursor on it, whatever page it falls on. */
  const addAtEnd = useCallback(() => {
    const at = rows.length;
    pendingFocus.current = [at, 0];          // the master-row picker of the new row
    setPage(Math.floor(at / pageSize) + 1);
    onInsertRow(at);
  }, [rows.length, pageSize, onInsertRow]);

  // ── right-click menu ──────────────────────────────────────────────
  const [ctx, setCtx] = useState(null);
  const onContext = useCallback((e, rowIdx) => {
    if (readOnly) return;
    e.preventDefault();
    setCtx({ x: e.clientX, y: e.clientY, rowIdx });
  }, [readOnly]);
  useEffect(() => {
    if (!ctx) return undefined;
    const close = () => setCtx(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); };
  }, [ctx]);

  return (
    <div className="plg-wrap">
      <style>{GRID_CSS}</style>

      <div ref={scrollRef} className="plg-scroll" onKeyDownCapture={onKeyDown}>
        <div style={{ width: bodyWidth, minWidth: '100%' }}>
          {/* header — two rows, exactly as the proforma grid */}
          <div className="plg-head">
            <div className="plg-hrow" style={{ height: GROUP_H }}>
              <div className="plg-ghdr plg-serial" style={{ width: 40, height: GROUP_H }} />
              {groups.map((g, i) => (
                <div
                  key={`${g.group}-${i}`}
                  className={`plg-ghdr${GROUP_TINT[g.group] ? ` ${GROUP_TINT[g.group]}` : ''}`}
                  style={{ width: g.width, height: GROUP_H }}
                >
                  {GROUP_TITLE[g.group]}
                </div>
              ))}
              {!readOnly && <div className="plg-ghdr plg-del" style={{ width: 32, height: GROUP_H }} />}
            </div>
            <div className="plg-hrow" style={{ height: LEAF_H }}>
              <div className="plg-lhdr plg-serial" style={{ width: 40, height: LEAF_H }}>#</div>
              {columns.map((c) => (
                <div
                  key={c.key}
                  className={`plg-lhdr${c.tint ? ` ${c.tint}` : ''}`}
                  style={{ width: c.width, height: LEAF_H }}
                  title={c.key === 'priceUomCode'
                    ? 'The unit this rate was agreed in — stored with the rate so a SQM price is never applied to a SQF document unconverted.'
                    : (c.key === 'thicknessMm'
                      ? 'Blank prices the whole process. A thickness overrides just that cell; the blank row still covers every other thickness.'
                      : undefined)}
                >
                  {c.title}
                </div>
              ))}
              {!readOnly && <div className="plg-lhdr plg-del" style={{ width: 32, height: LEAF_H }} />}
            </div>
          </div>

          {rows.length === 0 ? (
            <div className="plg-empty">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span style={{ fontSize: 12, color: '#94a3b8' }}>
                  Nothing overridden here — every rate follows the master.
                </span>}
              />
            </div>
          ) : (
            <>
              {rows.slice(first, last).map((row, i) => {
                const rowIdx = first + i;
                return (
                  <GridRow
                    key={rowIdx}
                    row={row}
                    rowIdx={rowIdx}
                    gid={gid}
                    columns={columns}
                    navIdxOf={navIdxOf}
                    opt={byRef.get(row.refId)}
                    readOnly={readOnly}
                    totalWidth={bodyWidth}
                    onPatch={onPatchRow}
                    onRemove={onRemoveRow}
                    onContext={onContext}
                    options={options}
                    onSearchItems={onSearchItems}
                    kindKey={kindKey}
                  />
                );
              })}
            </>
          )}
        </div>
      </div>

      {!readOnly && (
        <div className="plg-foot">
          <button type="button" className="plg-addbtn" onClick={addAtEnd}>
            + Add rate
          </button>
          <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
          <span><kbd>Ctrl</kbd>+<kbd>&apos;</kbd> fill down</span>
          <span><kbd>Ctrl</kbd>+<kbd>↓</kbd> insert row</span>
          <span><kbd>Ctrl</kbd>+<kbd>Del</kbd> delete row</span>
          <span>right-click for more</span>
          <span style={{ marginLeft: 'auto' }}>
            <Pagination
              size="small"
              current={page}
              pageSize={pageSize}
              total={rows.length}
              pageSizeOptions={PAGE_SIZES}
              showSizeChanger={rows.length > PAGE_SIZES[0]}
              showTotal={(t, [a, z]) => `${a}-${z} of ${t} rate${t === 1 ? '' : 's'}`}
              onChange={(next) => setPage(next)}
              onShowSizeChange={(_c, size) => { setPageSize(size); setPage(1); }}
            />
          </span>
        </div>
      )}
      {/* A read-only card still needs to reach page 2. */}
      {readOnly && rows.length > pageSize && (
        <div className="plg-foot">
          <span style={{ marginLeft: 'auto' }}>
            <Pagination
              size="small"
              current={page}
              pageSize={pageSize}
              total={rows.length}
              pageSizeOptions={PAGE_SIZES}
              showSizeChanger={rows.length > PAGE_SIZES[0]}
              showTotal={(t, [a, z]) => `${a}-${z} of ${t} rate${t === 1 ? '' : 's'}`}
              onChange={(next) => setPage(next)}
              onShowSizeChange={(_c, size) => { setPageSize(size); setPage(1); }}
            />
          </span>
        </div>
      )}

      {ctx && (
        <div className="plg-ctx" style={{ left: ctx.x, top: ctx.y }} onClick={(e) => e.stopPropagation()}>
          <button type="button" onClick={() => { onInsertRow(ctx.rowIdx); setCtx(null); }}>
            Insert row above<kbd>Ctrl ↑</kbd>
          </button>
          <button type="button" onClick={() => { onInsertRow(ctx.rowIdx + 1); setCtx(null); }}>
            Insert row below<kbd>Ctrl ↓</kbd>
          </button>
          <button type="button" onClick={() => { onDuplicateRow(ctx.rowIdx); setCtx(null); }}>
            Duplicate row
          </button>
          <div className="sep" />
          <button
            type="button"
            disabled={ctx.rowIdx === 0}
            onClick={() => {
              if (ctx.rowIdx > 0) onPatchRow(ctx.rowIdx, { ...rows[ctx.rowIdx - 1], refId: rows[ctx.rowIdx].refId });
              setCtx(null);
            }}
          >
            Copy rate from row above<kbd>Ctrl &apos;</kbd>
          </button>
          <div className="sep" />
          <button type="button" className="del" onClick={() => { onRemoveRow(ctx.rowIdx); setCtx(null); }}>
            Delete row<kbd>Ctrl Del</kbd>
          </button>
        </div>
      )}
    </div>
  );
}
